import React, { useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Modal,
  styled,
  TextField,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { gsap } from 'gsap';
import { contactForm, getProjectDetails } from '../../../../../api/services';

interface FormData {
  name: string;
  email: string;
  phoneNumber: string;
  message: string;
}

const CDN_URL =
  process.env.NEXT_PUBLIC_STORAGE_DN_URL || 'https://dprstorage.b-cdn.net';
const resolveImageUrl = (path?: string) => {
  if (!path) return '';
  return path.startsWith('http') ? path : `${CDN_URL}${path}`;
};

const schema = yup.object<FormData>().shape({
  name: yup.string().required().min(3),
  email: yup.string().email().required(),
  phoneNumber: yup
    .string()
    .matches(/^\d{10}$/, 'Must be 10 digits')
    .required(),
  message: yup.string().optional().default(''),
});

const ModalBox = styled(Box)({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 800,
  display: 'flex',
  overflow: 'hidden',
  backgroundColor: 'rgba(255,255,255,0.15)',
  backdropFilter: 'blur(20px)',
  borderRadius: 10,
  zIndex: 1000,
  color: 'white',
});

const ContentBox = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '50px 40px',
  background: 'rgba(15,15,15,0.7)',
  backdropFilter: 'blur(18px)',
  position: 'relative',
});

const InputContainer = styled(Box)({
  width: '100%',
  marginBottom: '10px',
});

const StyledTextField = styled(TextField)({
  width: '100%',
  '& .MuiInputBase-root': {
    color: '#fff',
    fontSize: '12px',
    minHeight: '36px',
    height: '36px',
  },
  '& input': {
    fontSize: '14px',
    height: '36px',
    padding: '8px 0',
  },
  '& label': {
    color: '#fff',
    fontSize: '14px',
  },
  '& label.Mui-focused': {
    color: '#9f8054',
  },
  '& .MuiInputLabel-shrink': {
    fontSize: '12px',
  },
  '& .MuiInput-underline:before': {
    borderBottomColor: 'rgba(255,255,255,0.6)',
  },
  '& .MuiInput-underline:hover:before': {
    borderBottomColor: '#9f8054',
  },
  '& .MuiInput-underline:after': {
    borderBottomColor: '#9f8054',
  },
  '& .MuiFormHelperText-root': {
    color: '#f44336',
    fontSize: '14px',
  },
});

interface LeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectSlug: string;
  projectTitle: string;
}

const LeadModal: React.FC<LeadModalProps> = ({
  isOpen,
  onClose,
  projectSlug,
  projectTitle,
}) => {
  const popupRef = useRef<HTMLDivElement | null>(null);

  // const { data: projectData } = useQuery({
  //   queryKey: ["projectDetails", projectSlug],
  //   queryFn: () => getProjectDetails(projectSlug),
  //   enabled: !!projectSlug,
  // });

  const { data: projectData } = useQuery({
    queryKey: ['projectDetails', projectSlug],
    queryFn: async () => {
      const res = await getProjectDetails(projectSlug);
      return res.data; // normalize to payload like other components
    },
    enabled: !!projectSlug,
  });

  // Prefer exterior image, fall back to interior or hero image if available
  const galleryPath =
    projectData?.gallerysection?.exterior?.c1r0?.[0] ||
    projectData?.gallerysection?.interior?.c1r0?.[0] ||
    projectData?.herosection?.projectimage ||
    '';

  const galleryImage = galleryPath ? resolveImageUrl(galleryPath) : '';

  // const galleryImage =
  //   projectData?.data?.gallerysection?.exterior?.c1r0?.[0]
  //     ? resolveImageUrl(projectData.data.gallerysection.exterior.c1r0[0])
  //     : "";

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
      message: '',
    },
  });

  // const mutation = useMutation({
  //   mutationFn: (formData: FormData) =>
  //     contactForm({
  //       ...formData,
  //       type: "project",
  //       // location: "Hyderabad",
  //       // projectName: projectTitle || "MEGALIEO",
  //     }),
  //   onSuccess: () => {
  //     toast.success("Lead Captured Successfully!");
  //     reset();
  //     onClose();
  //   },
  //   onError: () => toast.error("Network error. Please try again."),
  // });
  //   const onSubmit = (data: FormData) => mutation.mutate(data);

  const mutation = useMutation({
    mutationFn: (newFormData: FormData) => contactForm(newFormData),

    onSuccess: () => {
      toast.success('Lead Captured Successfully!');
      reset();
      onClose();
    },
    onError: () => {
      toast.error('Please check your network connection and try again.');
    },
  });

  const onSubmit: SubmitHandler<FormData> = (data) => {
    const newdata = {
      ...data,
      type: 'project',
      location: 'Hyderabad',
      projectSlug: projectSlug,
      leadSource: 'website',
    };
    mutation.mutate(newdata);
  };

  useEffect(() => {
    if (isOpen) {
      gsap.fromTo(
        popupRef.current,
        { scale: 0.7, opacity: 0, rotateY: 30 },
        { scale: 1, opacity: 1, rotateY: 0, duration: 0.4, ease: 'power3.out' },
      );
    } else {
      gsap.to(popupRef.current, {
        scale: 0.7,
        opacity: 0,
        rotateY: 30,
        duration: 0.6,
        ease: 'power3.in',
      });
    }
  }, [isOpen]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalBox ref={popupRef}>
        {/* Left image */}
        <Box
          sx={{
            flex: 1,
            display: { xs: 'none', md: 'block' },
            minHeight: 400,
            position: 'relative',
            backgroundColor: '#0f0f0f',
          }}
        >
          {galleryImage ? (
            <Box
              component="img"
              src={galleryImage}
              alt={projectTitle || 'Project image'}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : null}
        </Box>

        {/* Right form */}
        <ContentBox>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 15,
              right: 15,
              bgcolor: '#fff',
              color: 'primary.main',
              width: 35,
              height: 35,
              borderRadius: '50%',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: '#0f63a5',
                color: 'white',
                transform: 'scale(1.1) rotate(90deg)',
              },
            }}
          >
            <Close />
          </IconButton>

          <Typography
            variant="h4"
            sx={{ fontWeight: 600, mb: 1, color: 'white' }}
          >
            Book Your Site Visit
          </Typography>

          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ width: '100%' }}
            noValidate
          >
            <InputContainer>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <StyledTextField
                    {...field}
                    label="Name"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    variant="standard"
                    required
                  />
                )}
              />
            </InputContainer>

            <InputContainer>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <StyledTextField
                    {...field}
                    label="Email"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    variant="standard"
                    required
                  />
                )}
              />
            </InputContainer>

            <InputContainer>
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <StyledTextField
                    {...field}
                    label="Phone Number"
                    type="tel"
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber?.message}
                    variant="standard"
                    required
                  />
                )}
              />
            </InputContainer>

            <InputContainer>
              <Controller
                name="message"
                control={control}
                render={({ field }) => (
                  <StyledTextField
                    {...field}
                    label="Message"
                    placeholder="Write your message here..."
                    multiline
                    rows={2}
                    error={!!errors.message}
                    helperText={errors.message?.message}
                    variant="standard"
                  />
                )}
              />
            </InputContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  width: '50%',
                  bgcolor: '#fff',
                  borderRadius: '25px',
                  color: '#0f63a5',
                  '&:hover': {
                    bgcolor: '#0f63a5',
                    color: 'white',
                    transform: 'scale(1.05)',
                  },
                }}
              >
                Submit
              </Button>
            </Box>
          </form>
        </ContentBox>
      </ModalBox>
    </Modal>
  );
};

export default LeadModal;